import DutyStatusLog, {
    MALFUNCTION_STATUSES,
    NODEVICEID,
    NOUSERID
} from "../model/DutyStatusLog";
import { getUniqueDevicesFromLogs } from "../utils/malfunctionUtil";
import { getCurrentVehicle } from "../utils/device";
import * as common from "./common";
import { CancelablePromise as Promise } from "../utils/cancelablePromise";
import * as logger from "../utils/logger";
import { MobileActions } from "../store/mobile/actions";
import { dateTimeComparator } from "../utils/date";
import { DutyStatusLogService } from "../model/DutyStatusLogs";
import { FeedTypeKey } from "./common";
import BaseSync, { ISyncOptions } from "./base";
import type { THosMalfunction, THosStatus } from "hos-tools";

const UNIDENTIFIED_DRIVING_COMPLIANCE = "UnidentifiedDrivingCompliance";
const POSITIONING_COMPLIANCE = "PositioningCompliance";

interface IDutyStatusLogStorage {
    [key: string]: DutyStatusLog;
}

let getDutyStatusQueryParams = function (
    userId?: string | null,
    deviceId?: string | null,
    statuses?: THosStatus[] | null,
    fromDate?: Date | null,
    fromVersion?: string | null,
    toDate?: Date | null,
    malfunctionTypes?: THosMalfunction[] | null,
    includeBoundaryLogs = false,
    includeLocations = true,
    includeCompliance = true,
    resultsLimit: number | undefined = undefined
) {
    let queryParams = {
        typeName: "DutyStatusLog",
        fromVersion: fromVersion,
        search: {
            userSearch: userId ? { id: userId } : null,
            deviceSearch: deviceId ? { id: deviceId } : null,
            statuses,
            fromDate,
            toDate,
            includeCompliance,
            includeLocations,
            includeBoundaryLogs,
            malfunction: malfunctionTypes
        },
        resultsLimit
    };
    if (fromVersion === null) {
        delete queryParams.fromVersion;
    }
    return queryParams;
};

const diagnosticString = "Diagnostic";
const malfunctionString = "Malfunction";
const DIAGNOSTIC_KEY = "#diagnostic";
const MALFUNCTION_KEY = "#malfunction";
const MIN_VERSION = "0000000000000000";

// The maximum number of malfunction and diagnostic logs that can be downloaded for each device
const DEVICE_RESULTS_LIMIT = 1000;

let newMalfunctions = false;

export default class MalfunctionsSync extends BaseSync {
    malfunctionFeedVersion: any;
    private workedDays: { [key: string]: string[] };

    constructor(
        options: ISyncOptions,
        private readonly dutyStatusLogService: DutyStatusLogService
    ) {
        super(options);
    }

    processMalfunctions(
        localLogs: IIdDictionary<DutyStatusLog>,
        force: boolean
    ) {
        let oneCycleLogs = this.getOneCycleLogs(localLogs);
        let uniqDevices = getUniqueDevicesFromLogs(oneCycleLogs);
        this.workedDays = this.getUniqueDates(localLogs);
        return this.requestMalfunctionsForVehicles(
            uniqDevices,
            localLogs,
            force
        );
    }

    private getOneCycleLogs(logs: IDutyStatusLogStorage) {
        const retentionStartDateMs = this.hosProcess
            .getRetentionStartDate()
            .getTime();
        return Object.keys(logs).reduce((filtered, logKey) => {
            let log = logs[logKey];

            if (new Date(log.dateTime).getTime() > retentionStartDateMs) {
                filtered.push(log);
            }

            return filtered;
        }, [] as DutyStatusLog[]);
    }

    private getUniqueDates(logs: IDutyStatusLogStorage) {
        let oneCycleLogs = this.getOneCycleLogs(logs);
        let dates = Object.keys(oneCycleLogs).reduce(
            (groupedDate, logKey) => {
                let log: DutyStatusLog = oneCycleLogs[logKey];
                let deviceId =
                    log && log.device && (log.device.id || NODEVICEID);
                let match =
                    log &&
                    log.dateTime &&
                    log.dateTime.toString().match(/\d{4}-\d{2}-\d{2}/g);

                if (match && match[0]) {
                    if (groupedDate[deviceId]) {
                        if (groupedDate[deviceId].indexOf(match[0]) === -1) {
                            groupedDate[deviceId] =
                                groupedDate[deviceId].concat(match);
                        }
                    } else {
                        groupedDate[deviceId] = match;
                    }
                }

                return groupedDate;
            },
            {} as { [key: string]: any[] }
        );

        return dates;
    }

    private requestBoundaryMalfunctionsForVehicles(
        uniqDevices: string[],
        oneCycle: Date
    ): Promise<DutyStatusLog[]> {
        let malfunctionCalls: any[] = [];
        let statusSearch: THosStatus[] = [POSITIONING_COMPLIANCE];
        let diagnosticTypes: THosMalfunction[] = [
            "Diagnostic",
            "SystemDiagnosticClear"
        ];
        let malfunctionTypes: THosMalfunction[] = [
            "Malfunction",
            "UserMalfunctionClear"
        ];

        uniqDevices.forEach((deviceId) => {
            // Get positioningcompliance boundary logs
            if (deviceId !== NODEVICEID) {
                malfunctionCalls.push([
                    "GetFeed",
                    getDutyStatusQueryParams(
                        NOUSERID,
                        deviceId,
                        statusSearch,
                        oneCycle,
                        null,
                        oneCycle,
                        diagnosticTypes,
                        true,
                        false,
                        false
                    )
                ]);
                malfunctionCalls.push([
                    "GetFeed",
                    getDutyStatusQueryParams(
                        NOUSERID,
                        deviceId,
                        statusSearch,
                        oneCycle,
                        null,
                        oneCycle,
                        malfunctionTypes,
                        true,
                        false,
                        false
                    )
                ]);
            }
        });

        if (!malfunctionCalls.length) {
            return Promise.resolve([]);
        }

        return new Promise((resolve, reject) => {
            this.api.multiCall(
                malfunctionCalls,
                (results) => {
                    let logsArray: DutyStatusLog[] = [];

                    results.forEach((resultObj) => {
                        logsArray = logsArray.concat(...resultObj.data);
                    });

                    logsArray =
                        this.dutyStatusLogService.convertDutyStatusLogsFromServer(
                            logsArray
                        );
                    let oneCycleString = oneCycle.toISOString();
                    // Anything > oneCycle will already be fetched in the normal sync
                    logsArray = logsArray.filter(
                        (log) => log.dateTime < oneCycleString
                    );
                    resolve(logsArray);
                },
                reject
            );
        });
    }

    private requestMalfunctionsForVehicles(
        uniqDevices,
        localLogs,
        force
    ): Promise<{ logs: DutyStatusLog[]; version: string }> {
        let oneCycle = this.hosProcess.getRetentionStartDate();
        let version = force
            ? MIN_VERSION
            : common.getFeedVersion(
                  this.api,
                  this.store,
                  FeedTypeKey.MALFUNCTION
              ) || MIN_VERSION;
        let malfunctionCalls: any[] = [];
        const resultsLimit = DEVICE_RESULTS_LIMIT;
        uniqDevices.forEach((deviceId) => {
            if (deviceId !== NODEVICEID) {
                malfunctionCalls.push([
                    "GetFeed",
                    getDutyStatusQueryParams(
                        NOUSERID,
                        deviceId,
                        MALFUNCTION_STATUSES,
                        oneCycle,
                        version,
                        undefined,
                        undefined,
                        undefined,
                        false,
                        undefined,
                        resultsLimit
                    )
                ]);
            }
        });

        if (!malfunctionCalls.length) {
            return Promise.resolve({ logs: [], version });
        }

        return new Promise((resolve, reject) => {
            this.api.multiCall(
                malfunctionCalls,
                (results) => {
                    const devicesWithExcessiveLogs: string[] = [];
                    results.forEach((result, index) => {
                        if (result.data?.length >= resultsLimit) {
                            const call = malfunctionCalls[index][1];
                            if (call?.search?.deviceSearch?.id) {
                                devicesWithExcessiveLogs.push(
                                    call.search.deviceSearch.id
                                );
                            }
                        }
                    });

                    if (devicesWithExcessiveLogs.length) {
                        logger.event(
                            logger.Event.EXCESSIVEDUTYSTATUSLOGS,
                            `Downloaded malfunction/diagnostic logs for devices [${ devicesWithExcessiveLogs.join(",") }] exceed the resultsLimit (${ resultsLimit })`,
                            this.currentUser
                        );
                    }

                    let latestFeedVersion = this.getFeedVersion(results);
                    this.malfunctionFeedVersion = latestFeedVersion;
                    let malfunctions = this.getLogs(results);
                    let noNewMalfunctions = false;

                    logger.log(
                        logger.Source.SYNC,
                        `Downloaded ${ malfunctions.length } malfunction/diagnostic HOS records`,
                        this.api.getUser()
                    );

                    if (!malfunctions.length) {
                        noNewMalfunctions = true;
                        malfunctions = this.getLogsFromLocal(localLogs);
                    }

                    let sanitizedMalfunctions =
                        this.sanitizeMalfunctions(malfunctions);
                    this.isInMalfunctionOrDiagnostic(sanitizedMalfunctions);

                    if (force || version === MIN_VERSION) {
                        return this.requestBoundaryMalfunctionsForVehicles(
                            uniqDevices,
                            oneCycle
                        )
                            .then((boundaryLogs) => {
                                if (noNewMalfunctions) {
                                    resolve({
                                        logs: boundaryLogs,
                                        version: latestFeedVersion
                                    });
                                } else {
                                    resolve({
                                        logs: sanitizedMalfunctions.concat(
                                            boundaryLogs
                                        ),
                                        version: latestFeedVersion
                                    });
                                }
                            })
                            .catch(reject);
                    }
                    resolve({
                        logs: noNewMalfunctions
                            ? []
                            : sanitizedMalfunctions || [],
                        version: latestFeedVersion
                    });
                    return undefined;
                },
                reject
            );
        });
    }

    private getFeedVersion(responseArray) {
        let intArray = responseArray.map((el) => parseInt(el.toVersion, 16));
        let maxVal = intArray.reduce(
            (max, curr) => Math.max(max, curr),
            -Infinity
        );
        let index = intArray.indexOf(maxVal);

        return responseArray[index].toVersion;
    }

    private getLogs(
        results,
        fromDriversMalfunctionDiagnosticCall = false
    ): DutyStatusLog[] {
        let logsArray: DutyStatusLog[] = [];
        if (fromDriversMalfunctionDiagnosticCall) {
            logsArray = [...results];
        } else {
            results.forEach((resultObj) => {
                logsArray = logsArray.concat(...resultObj.data);
            });
        }

        if (logsArray && logsArray.length) {
            newMalfunctions = true;
        }
        return this.dutyStatusLogService.convertDutyStatusLogsFromServer(
            logsArray
        );
    }

    private getLogsFromLocal(
        localLogs: IIdDictionary<DutyStatusLog>
    ): DutyStatusLog[] {
        newMalfunctions = false;
        return Object.keys(localLogs).reduce((filtered, logKey) => {
            let localLog = localLogs[logKey];

            if (
                localLog.malfunction &&
                localLog.malfunction.match(/Malfunction|Diagnostic/)
            ) {
                filtered.push(localLog);
            }

            return filtered;
        }, [] as DutyStatusLog[]);
    }

    private sanitizeMalfunctions(results: DutyStatusLog[]): DutyStatusLog[] {
        return results.filter((malfunction) => {
            if (
                malfunction &&
                malfunction.dateTime &&
                malfunction.malfunction
            ) {
                if (
                    malfunction.malfunction.indexOf("Clear") > -1 ||
                    malfunction.status === POSITIONING_COMPLIANCE ||
                    malfunction.status === UNIDENTIFIED_DRIVING_COMPLIANCE
                ) {
                    return true;
                }
                let matches = malfunction.dateTime.match(/\d{4}-\d{2}-\d{2}/g);
                let date = matches !== null ? matches[0] : "";
                let id =
                    malfunction.device &&
                    (malfunction.device.id || "NoDeviceId");

                return Object.keys(this.workedDays).some((vehicleId) => {
                    let workDates = this.workedDays[vehicleId];
                    return workDates.indexOf(date) !== -1 && id === vehicleId;
                });
            }
            return false;
        });
    }

    private isInMalfunctionOrDiagnostic(logsMap: DutyStatusLog[]) {
        const unidentifiedDrivingRetentionStartDateMs = this.hosProcess
            .getUnidentifiedDrivingRetentionStartDate(
                this.store.getState().data.ELDJurisdiction === "CANELD"
            )
            .getTime();
        const currentVehicleLogsMap = this.getGroupedAndSortedMalfunctions(
            logsMap,
            unidentifiedDrivingRetentionStartDateMs
        );
        let currentVehicle = getCurrentVehicle();
        let isInMalfunction = false;
        let isInDiagnostic = false;
        let malfunctionMapKeysArray = Object.keys(currentVehicleLogsMap);

        if (currentVehicle) {
            isInDiagnostic = malfunctionMapKeysArray.some((logsKey) => {
                let logsGroup = currentVehicleLogsMap[logsKey];

                return this.isInDiagnostic(
                    logsGroup[0],
                    unidentifiedDrivingRetentionStartDateMs
                );
            });

            isInMalfunction = malfunctionMapKeysArray.some((logsKey) => {
                let logsGroup = currentVehicleLogsMap[logsKey];
                let firstLog = logsGroup[0];

                return (
                    firstLog.malfunction === malfunctionString &&
                    MALFUNCTION_STATUSES.indexOf(firstLog.status) !== -1
                );
            });
        }

        if (
            this.arrayContainsStringWithSubstring(
                malfunctionMapKeysArray,
                MALFUNCTION_KEY
            )
        ) {
            this.store.dispatch(
                MobileActions.setEldMalfunction(isInMalfunction)
            );
        }

        if (
            this.arrayContainsStringWithSubstring(
                malfunctionMapKeysArray,
                DIAGNOSTIC_KEY
            )
        ) {
            this.store.dispatch(MobileActions.setEldDiagnostic(isInDiagnostic));
        }
    }

    private getGroupedAndSortedMalfunctions(
        logs: DutyStatusLog[],
        unidentifiedDrivingRetentionStartDateMs: number
    ) {
        let currentVehicle = getCurrentVehicle() || { id: NODEVICEID };
        let currentVehicleLogs = logs
            .filter((log) => log.device.id === currentVehicle.id)
            .sort(dateTimeComparator(false));

        let resultMap = currentVehicleLogs.reduce(
            (grouped, log) => {
                let logKey = `${ log.status }${ log.isDiagnostic() ? DIAGNOSTIC_KEY : MALFUNCTION_KEY }`;

                if (grouped[logKey]) {
                    grouped[logKey].push(log);
                } else {
                    grouped[logKey] = [log];
                }

                return grouped;
            },
            {} as { [key: string]: DutyStatusLog[] }
        );

        if (newMalfunctions) {
            this.checkForDiagnosticsViewedState(
                resultMap,
                unidentifiedDrivingRetentionStartDateMs
            );
        }

        return resultMap;
    }

    private checkForDiagnosticsViewedState(
        logsMap: { [key: string]: DutyStatusLog[] },
        unidentifiedDrivingRetentionStartDateMs: number
    ) {
        const statusesToCheck = ["PowerCompliance", "EngineSyncCompliance"];
        let checkDiagnostic = (log: DutyStatusLog) => {
            if (!log.malfunction) {
                return false;
            }
            if (
                this.isInDiagnostic(
                    log,
                    unidentifiedDrivingRetentionStartDateMs
                )
            ) {
                return true;
            }
            // Trigger indicator for new power and engine sync compliance even if they've been cleared
            return (
                statusesToCheck.indexOf(log.status) !== -1 && log.isDiagnostic()
            );
        };

        let newDiagnostics = Object.keys(logsMap).some((logsKey) =>
            checkDiagnostic(logsMap[logsKey][0]));

        if (newDiagnostics) {
            this.store.dispatch(
                MobileActions.setDiagnosticViewed(!newDiagnostics)
            );
        }
    }

    private arrayContainsStringWithSubstring(arr, substring) {
        return arr.some((str) => str.indexOf(substring) > -1);
    }

    private isInDiagnostic(
        log: DutyStatusLog,
        unidentifiedDrivingRetentionStartDateMs: number
    ) {
        return (
            log.malfunction === diagnosticString &&
            MALFUNCTION_STATUSES.indexOf(log.status) !== -1 &&
            log.status !== POSITIONING_COMPLIANCE &&
            (log.status !== UNIDENTIFIED_DRIVING_COMPLIANCE ||
                new Date(log.dateTime).getTime() >=
                    unidentifiedDrivingRetentionStartDateMs)
        );
    }
}
